import { useEffect, useState } from 'react';
import {
  listarIntegracoes, atualizarIntegracao,
  testarIntegracaoReq, limparCredenciaisIntegracao,
} from '../api';
import { PageHeader, Loading, Empty, Alert } from '../components/common';
import { fmtData } from '../lib/format';

const CATEGORIAS_LABEL = {
  serpro:  'SERPRO / Receita Federal',
  sieg:    'Sieg (Notas Tomadas)',
  atrio:   'Ecossistema Átrio',
  externo: 'APIs Externas',
};

export default function Integracoes() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [mensagem, setMensagem] = useState(null);
  const [configurando, setConfigurando] = useState(null);   // integração sendo editada

  const carregar = () => {
    setLoading(true);
    listarIntegracoes()
      .then(r => setItems(r.items || []))
      .catch(e => setErro(e.message))
      .finally(() => setLoading(false));
  };
  useEffect(carregar, []);

  const testar = async (codigo) => {
    setMensagem({ tipo: 'info', txt: `Testando ${codigo}…` });
    try {
      const r = await testarIntegracaoReq(codigo);
      setMensagem({
        tipo: r.ok ? 'success' : 'danger',
        txt: r.ok
          ? `${codigo}: OK (${r.latencia_ms}ms)`
          : `${codigo}: ${r.erro || 'falhou'}`,
      });
      carregar();
    } catch (e) {
      setMensagem({ tipo: 'danger', txt: e.message });
    }
  };

  if (loading) return <Loading texto="Carregando integrações…" />;

  // Agrupa por categoria preservando ordem (backend já manda na ordem certa)
  const grupos = items.reduce((acc, it) => {
    let g = acc.find(x => x.cat === it.categoria);
    if (!g) {
      g = { cat: it.categoria, items: [] };
      acc.push(g);
    }
    g.items.push(it);
    return acc;
  }, []);

  return (
    <>
      <PageHeader
        title="Integrações Externas"
        sub="Centraliza credenciais (vault A1) e testa conexões com SERPRO, Sieg, Átrio Ecossistema, APIs externas"
        actions={<button onClick={carregar}>↻ Recarregar</button>}
      />

      {erro && <Alert tipo="danger">{erro}</Alert>}
      {mensagem && <Alert tipo={mensagem.tipo}>{mensagem.txt}</Alert>}

      {grupos.map(g => (
        <div key={g.cat} style={{ marginBottom: 24 }}>
          <h2 style={{
            fontSize: 12,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            color: 'var(--gold)',
            marginBottom: 10,
            fontFamily: 'var(--font-data)',
          }}>
            {CATEGORIAS_LABEL[g.cat] || g.cat}
          </h2>
          <div style={{ display: 'grid', gap: 12 }}>
            {g.items.map(it => (
              <IntegracaoCard
                key={it.codigo}
                integ={it}
                onConfigurar={() => setConfigurando(it)}
                onTestar={() => testar(it.codigo)}
              />
            ))}
          </div>
        </div>
      ))}

      {configurando && (
        <ConfigurarModal
          integ={configurando}
          onFechar={() => setConfigurando(null)}
          onSalvo={() => { setConfigurando(null); carregar(); }}
        />
      )}
    </>
  );
}

// =============================================================================
// Card de integração
// =============================================================================
function IntegracaoCard({ integ, onConfigurar, onTestar }) {
  const statusInfo = (() => {
    if (!integ.ativo) return { cor: 'var(--text-3)', txt: '○ Desligada' };
    if (!integ.credenciais_configuradas && integ.config_publica?.auth_tipo !== 'none')
      return { cor: 'var(--warning)', txt: '⚠ Credencial faltando' };
    if (integ.ultima_conexao_ok === true) return { cor: 'var(--success)', txt: '✓ Conectada' };
    if (integ.ultima_conexao_ok === false) return { cor: 'var(--danger)', txt: '✗ Falhou' };
    return { cor: 'var(--text-2)', txt: '— Não testada' };
  })();

  return (
    <div className="card" style={{
      padding: 16,
      borderLeft: `3px solid ${statusInfo.cor}`,
      display: 'flex',
      gap: 16,
      alignItems: 'flex-start',
    }}>
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
          <strong style={{ fontSize: 14 }}>{integ.nome}</strong>
          <span style={{
            fontSize: 10, fontWeight: 600, color: statusInfo.cor,
            fontFamily: 'var(--font-data)',
            letterSpacing: '0.04em',
          }}>{statusInfo.txt}</span>
          {integ.ambiente && (
            <span className="badge badge--neutral" style={{ fontSize: 9 }}>
              {integ.ambiente.toUpperCase()}
            </span>
          )}
        </div>
        <div className="small muted" style={{ marginBottom: 6 }}>{integ.descricao}</div>

        {/* Detalhes config + credenciais */}
        <div className="small" style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontFamily: 'var(--font-data)' }}>
          {integ.config_publica?.auth_tipo && (
            <span className="muted">auth: {integ.config_publica.auth_tipo}</span>
          )}
          {integ.credenciais_preview && (
            <span style={{ color: 'var(--gold)' }}>{integ.credenciais_preview}</span>
          )}
          {integ.ultima_conexao_em && (
            <span className="muted">
              último teste: {fmtData(integ.ultima_conexao_em)}
              {integ.ultima_conexao_ms != null && ` · ${integ.ultima_conexao_ms}ms`}
            </span>
          )}
        </div>

        {integ.ultimo_erro_msg && integ.ultima_conexao_ok === false && (
          <div className="small" style={{
            marginTop: 6, color: 'var(--danger)',
            fontFamily: 'var(--font-data)', fontSize: 11,
          }}>
            ✗ {integ.ultimo_erro_msg}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 6 }}>
        <button onClick={onTestar}>Testar</button>
        <button className="primary" onClick={onConfigurar}>Configurar</button>
      </div>
    </div>
  );
}

// =============================================================================
// Modal de configuração
// =============================================================================
function ConfigurarModal({ integ, onFechar, onSalvo }) {
  const campos = integ.config_publica?.campos_credencial || ['api_key'];
  const [valores, setValores] = useState(
    Object.fromEntries(campos.map(c => [c, '']))
  );
  const [ambiente, setAmbiente] = useState(integ.ambiente || 'sandbox');
  const [ativo, setAtivo] = useState(integ.ativo);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState(null);

  const salvar = async () => {
    setErro(null);
    setSalvando(true);
    try {
      const credenciais = {};
      for (const [k, v] of Object.entries(valores)) {
        if (v && v.trim()) credenciais[k] = v.trim();
      }
      await atualizarIntegracao(integ.codigo, {
        credenciais: Object.keys(credenciais).length > 0 ? credenciais : undefined,
        ambiente,
        ativo,
      });
      onSalvo();
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(false);
    }
  };

  const limpar = async () => {
    if (!confirm('Apagar credenciais salvas? Próximo teste vai falhar até reconfigurar.')) return;
    try {
      await limparCredenciaisIntegracao(integ.codigo);
      onSalvo();
    } catch (e) {
      setErro(e.message);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(8,8,10,0.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200,
    }} onClick={onFechar}>
      <div className="card" style={{ width: 600, maxHeight: '90vh', overflow: 'auto', padding: 20 }} onClick={e => e.stopPropagation()}>
        <div className="flex mb-2">
          <h2 style={{ margin: 0, color: 'var(--gold)' }}>{integ.nome}</h2>
          <div style={{ flex: 1 }} />
          <button onClick={onFechar}>Fechar</button>
        </div>

        <p className="small muted" style={{ marginBottom: 16 }}>{integ.descricao}</p>

        {erro && <Alert tipo="danger">{erro}</Alert>}

        {/* Campos de credencial */}
        {campos.length > 0 && integ.config_publica?.auth_tipo !== 'none' && (
          <>
            <h3 style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--gold)', marginTop: 16, marginBottom: 10 }}>
              Credenciais
            </h3>
            <p className="small muted" style={{ marginBottom: 8 }}>
              Armazenadas cifradas via vault AES-256-GCM. Plaintext nunca persistido.
            </p>
            {campos.map(c => (
              <div key={c} style={{ marginBottom: 10 }}>
                <label className="small muted" style={{ display: 'block', marginBottom: 4 }}>
                  {c}
                </label>
                <input
                  type="password"
                  value={valores[c]}
                  onChange={e => setValores({ ...valores, [c]: e.target.value })}
                  placeholder={
                    integ.credenciais_configuradas
                      ? '(deixe vazio pra manter o atual)'
                      : 'Cole aqui'
                  }
                  autoComplete="off"
                />
              </div>
            ))}
          </>
        )}

        <h3 style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--gold)', marginTop: 16, marginBottom: 10 }}>
          Configuração
        </h3>
        <div className="flex" style={{ gap: 12, marginBottom: 10 }}>
          <div>
            <label className="small muted">Ambiente</label>
            <select value={ambiente} onChange={e => setAmbiente(e.target.value)} style={{ width: 160 }}>
              <option value="sandbox">Sandbox / Teste</option>
              <option value="producao">Produção</option>
            </select>
          </div>
          <label className="small" style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 18 }}>
            <input
              type="checkbox"
              checked={ativo}
              onChange={e => setAtivo(e.target.checked)}
              style={{ width: 'auto' }}
            />
            Integração ativa
          </label>
        </div>

        {/* Doc inline */}
        {integ.config_publica?.doc && (
          <div className="small muted" style={{ marginTop: 12 }}>
            📖 Documentação: <a href={integ.config_publica.doc} target="_blank" rel="noopener noreferrer">{integ.config_publica.doc}</a>
          </div>
        )}

        {/* URLs config (read-only) */}
        <details style={{ marginTop: 16 }}>
          <summary className="small muted" style={{ cursor: 'pointer' }}>
            Config técnica (URLs, scopes)
          </summary>
          <pre style={{
            background: 'var(--void-2)',
            padding: 10,
            borderRadius: 4,
            fontSize: 10,
            fontFamily: 'var(--font-data)',
            marginTop: 6,
            overflow: 'auto',
          }}>
            {JSON.stringify(integ.config_publica, null, 2)}
          </pre>
        </details>

        <div className="flex" style={{ marginTop: 20, gap: 8, justifyContent: 'space-between' }}>
          {integ.credenciais_configuradas && (
            <button className="danger" onClick={limpar}>Limpar credenciais</button>
          )}
          <div style={{ flex: 1 }} />
          <button onClick={onFechar} disabled={salvando}>Cancelar</button>
          <button className="primary" onClick={salvar} disabled={salvando}>
            {salvando ? 'Salvando…' : 'Salvar'}
          </button>
        </div>
      </div>
    </div>
  );
}
